import React, { useState } from "react";
import { Button, Box, Typography } from "@mui/material";
import { useAuth } from "../context/AuthContext";

// tiny JWT decoder (no external deps)
function decodeJwt(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

export default function SsoDebug() {
  const { getAccessToken, authData, login, logout, isSso, loading } = useAuth();
  const [token, setToken] = useState("");
  const [claims, setClaims] = useState(null);
  const [apiResult, setApiResult] = useState(null);
  const [error, setError] = useState("");

  const handleGetToken = async () => {
    setError("");
    try {
      const t = await getAccessToken();
      setToken(t || "");
      setClaims(decodeJwt(t || "") || {});
    } catch (e) {
      setError(String(e));
      setToken("");
      setClaims(null);
    }
  };

  
  const callApi = async () => {
    setError("");
    setApiResult(null);
    try {
      const t = token || (await getAccessToken());
      const res = await fetch("https://api.axillonaerospace.info/api/users/hello-sso/", {
        headers: isSso && t ? { Authorization: `Bearer ${t}` } : {},
        credentials: isSso ? "omit" : "include",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(JSON.stringify(body) || res.statusText);
      setApiResult(body);
    } catch (e) {
      setError(String(e));
    }
  };

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(token);
    } catch {}
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
      <Typography variant="h5" gutterBottom>SSO Debug</Typography>
      <Typography variant="body2" gutterBottom>
        Mode: <b>{isSso ? "Microsoft SSO" : "Legacy (cookies)"}</b>
      </Typography>
      <Typography variant="body2" gutterBottom>
        User: {authData?.name || authData?.email || "(not signed in)"}
      </Typography>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 2 }}>
        <Button variant="contained" onClick={() => login()} disabled={loading}>
          {loading ? "Working…" : isSso ? "Sign in with Microsoft" : "Login"}
        </Button>
        <Button variant="outlined" onClick={logout}>Logout</Button>
        <Button variant="outlined" onClick={handleGetToken}>
          Get Access Token
        </Button>
        <Button variant="outlined" onClick={callApi}>
          Call /hello-sso/
        </Button>
        <Button variant="text" onClick={copyToken} disabled={!token}>
          Copy Token
        </Button>
      </Box>

      {error && (
        <Box sx={{ my: 2 }}>
          <Typography color="error" variant="body2">Error: {error}</Typography>
        </Box>
      )}

      <Box sx={{ my: 2 }}>
        <Typography variant="subtitle1">Access Token (truncated):</Typography>
        <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", background: "#111", color: "#ddd", padding: 8, borderRadius: 6 }}>
          {token ? token.slice(0, 64) + "…" : "(none yet)"}
        </pre>
        <Typography variant="caption" color="text.secondary">
          ⚠️ Don’t log full tokens in production.
        </Typography>
      </Box>

      <Box sx={{ my: 2 }}>
        <Typography variant="subtitle1">Decoded Claims:</Typography>
        <pre style={{ whiteSpace: "pre-wrap", background: "#111", color: "#ddd", padding: 8, borderRadius: 6 }}>
{JSON.stringify(claims || {}, null, 2)}
        </pre>
      </Box>

      <Box sx={{ my: 2 }}>
        <Typography variant="subtitle1">/hello-sso/ Response:</Typography>
        <pre style={{ whiteSpace: "pre-wrap", background: "#111", color: "#ddd", padding: 8, borderRadius: 6 }}>
{JSON.stringify(apiResult || {}, null, 2)}
        </pre>
      </Box>
    </Box>
  );
}